import { useEffect, useState } from 'react';
import type { HotspotTrack } from '../types';
import { fetchHotspotImageBlobUrl } from '../api';

interface HotspotModalProps {
  jobId: string;
  hotspot: HotspotTrack | null;
  onClose: () => void;
}

/**
 * Detail panel shown when a hotspot is clicked. Feature hotspots (blue)
 * show title + body text; damage hotspots (red) additionally show the
 * cropped original arch frame with the damage box drawn on it, fetched as
 * an authenticated blob URL.
 */
export function HotspotModal({ jobId, hotspot, onClose }: HotspotModalProps) {
  const isDamage = hotspot?.kind === 'damage';
  const imageName = isDamage ? hotspot?.image : undefined;

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageUrl(null);
    setImageError(false);
    if (!imageName) return;
    const ac = new AbortController();
    let url: string | null = null;
    fetchHotspotImageBlobUrl(jobId, imageName, ac.signal)
      .then((u) => {
        if (ac.signal.aborted) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setImageUrl(u);
      })
      .catch(() => {
        if (!ac.signal.aborted) setImageError(true);
      });
    return () => {
      ac.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [jobId, imageName]);

  if (!hotspot) return null;

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
      // The viewer container grabs the pointer (setPointerCapture) on
      // pointerdown to drive the drag-scrub, which otherwise swallows the
      // modal's click. Stop the gesture at the modal boundary so the X /
      // backdrop close reliably.
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                isDamage ? 'bg-red-500' : 'bg-indigo-500'
              }`}
              aria-hidden
            />
            <div className="text-sm font-semibold text-slate-900 truncate">{hotspot.label}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {isDamage && (
          <div className="bg-slate-900/5 border-b border-slate-100">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={hotspot.label}
                className="block w-full max-h-72 object-contain bg-slate-900"
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-slate-400">
                {imageError ? 'Image unavailable' : 'Loading image…'}
              </div>
            )}
          </div>
        )}
        <div className="px-5 py-4 text-sm text-slate-700 whitespace-pre-line">
          {hotspot.text || 'No description available.'}
        </div>
      </div>
    </div>
  );
}
